/**
 * CalendarioPage — Matriz mensual embarcaciones × días.
 * Endpoint: GET /turismo/charter/disponibilidad/matriz?anio&mes
 *
 * Cada celda: libre (verde tenue) · pasadía (azul) · charter (morado).
 * Click en celda ocupada → navega al detalle.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, IconButton, CircularProgress, Tooltip, Chip, Button,
} from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { getFirebaseAuthHeaders } from '../common/firebaseAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

async function getAuthHeaders() { return getFirebaseAuthHeaders(); }
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// Colores vivos, texto blanco sobre fondo saturado — la celda comunica el
// estado de un vistazo aún con la matriz completa a pantalla.
// Labels abreviados de 3 letras que caben en la celda de 34px.
const CELL_STYLES = {
  libre:   { bg: '#22c55e', fg: '#ffffff', border: '#16a34a', label: 'LIB' },
  pasadia: { bg: '#3b82f6', fg: '#ffffff', border: '#2563eb', label: 'PAS' },
  charter: { bg: '#a855f7', fg: '#ffffff', border: '#9333ea', label: 'CHT' },
};

export default function CalendarioPage() {
  const navigate = useNavigate();
  const { empresaId } = useOutletContext() || {};
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Filtro por tipo de celda. 'todos' = sin filtro. Al filtrar, las celdas
  // que NO coinciden se atenúan (visible pero apagada) en vez de ocultarse
  // para mantener el layout y el contexto de la matriz.
  const [filtro, setFiltro] = useState('todos');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/charter/disponibilidad/matriz?empresa_id=${empresaId}&anio=${anio}&mes=${mes}`,
        { headers: await getAuthHeaders() }
      );
      if (r.ok) setData(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId, anio, mes]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarMes = (delta) => {
    let m = mes + delta, a = anio;
    if (m > 12) { m = 1; a += 1; }
    if (m < 1)  { m = 12; a -= 1; }
    setMes(m); setAnio(a);
  };

  const hoyIso = hoy.toISOString().slice(0, 10);
  const esHoy = (d) => `${anio}-${String(mes).padStart(2,'0')}-${String(d).padStart(2,'0')}` === hoyIso;

  const onCellClick = (celda) => {
    if (!celda || celda.estado === 'libre') return;
    if (celda.estado === 'charter' && celda.ref_id) navigate(`/charter/${celda.ref_id}`);
    if (celda.estado === 'pasadia' && celda.ref_id) navigate('/pasadias/salidas'); // best effort
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            Calendario de embarcaciones
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Vista mensual · pasadías (azul) y charters (morado)
          </Typography>
        </Box>
        <Paper sx={{ display: 'flex', alignItems: 'center', px: 1, borderRadius: 3 }}>
          <IconButton onClick={() => cambiarMes(-1)}><ChevronLeftIcon /></IconButton>
          <Typography sx={{ minWidth: 160, textAlign: 'center', fontWeight: 700, color: '#7c3aed' }}>
            {MESES[mes - 1]} {anio}
          </Typography>
          <IconButton onClick={() => cambiarMes(1)}><ChevronRightIcon /></IconButton>
        </Paper>
        <Button variant="outlined" size="small" startIcon={<TodayIcon />}
          onClick={() => { setAnio(hoy.getFullYear()); setMes(hoy.getMonth() + 1); }}
          sx={{ textTransform: 'none', borderColor: '#7c3aed', color: '#7c3aed' }}
        >
          Hoy
        </Button>
      </Box>

      {/* Chips-filtro. Click alterna entre "activo" y "todos" para el tipo. */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600, mr: 0.5 }}>
          Filtrar:
        </Typography>
        {[
          { key: 'todos',   label: 'Todos',   bg: '#f1f5f9', fg: '#0f172a', border: '#cbd5e1' },
          { key: 'libre',   label: 'Libre',   ...CELL_STYLES.libre },
          { key: 'pasadia', label: 'Pasadía', ...CELL_STYLES.pasadia },
          { key: 'charter', label: 'Charter', ...CELL_STYLES.charter },
        ].map(chip => {
          const activo = filtro === chip.key;
          return (
            <Chip key={chip.key} label={chip.label} size="small" clickable
              onClick={() => setFiltro(activo ? 'todos' : chip.key)}
              sx={{
                bgcolor: chip.bg, color: chip.fg,
                border: `1px solid ${chip.border}`,
                fontWeight: activo ? 800 : 500,
                boxShadow: activo ? `0 0 0 2px ${chip.border}` : 'none',
                transition: 'box-shadow 0.15s, transform 0.15s',
                '&:hover': { transform: 'translateY(-1px)' },
              }} />
          );
        })}
        {filtro !== 'todos' && (
          <Typography sx={{ fontSize: 11, color: '#94a3b8', ml: 1 }}>
            (click en el chip activo para quitar el filtro)
          </Typography>
        )}
      </Box>

      {loading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#7c3aed' }} />
        </Box>
      ) : data.embarcaciones.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ color: '#64748b' }}>
            No hay embarcaciones activas para mostrar.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflowX: 'auto', p: 1 }}>
          <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
            {/* Cabecera de días */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `180px repeat(${data.dias.length}, 34px)`, gap: '2px' }}>
              <Box sx={{ p: 0.5, fontSize: 11, fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}>
                Embarcación
              </Box>
              {data.dias.map(d => (
                <Box key={d} sx={{
                  p: 0.5, fontSize: 10, fontWeight: 700, textAlign: 'center',
                  bgcolor: esHoy(d) ? '#7c3aed' : '#f8fafc',
                  color: esHoy(d) ? '#fff' : '#64748b',
                  borderRadius: 1,
                }}>
                  {d}
                </Box>
              ))}
            </Box>

            {/* Filas de embarcaciones */}
            {data.embarcaciones.map(e => (
              <Box key={e.id} sx={{
                display: 'grid',
                gridTemplateColumns: `180px repeat(${data.dias.length}, 34px)`,
                gap: '2px', mt: '2px',
              }}>
                <Box sx={{
                  p: 1, fontSize: 12, fontWeight: 600, color: '#0f172a',
                  bgcolor: '#fff', borderLeft: '3px solid #7c3aed', display: 'flex',
                  alignItems: 'center', gap: 0.5,
                }}>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.nombre}
                  </Box>
                  <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>· {e.capacidad}p</Typography>
                </Box>
                {data.dias.map(d => {
                  const celda = data.celdas[e.id]?.[d] || { estado: 'libre' };
                  const st = CELL_STYLES[celda.estado] || CELL_STYLES.libre;
                  const clickable = celda.estado !== 'libre';
                  // Filtro: si hay uno activo y la celda no coincide, atenuar
                  // (sin ocultar — mantiene el layout de la matriz intacto).
                  const filtrada = filtro !== 'todos' && celda.estado !== filtro;
                  const cell = (
                    <Box onClick={() => !filtrada && onCellClick(celda)}
                      sx={{
                        height: 32,
                        bgcolor: st.bg, color: st.fg,
                        border: `1px solid ${st.border}`,
                        borderRadius: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 800, letterSpacing: 0.3,
                        opacity: filtrada ? 0.15 : 1,
                        filter: filtrada ? 'grayscale(1)' : 'none',
                        cursor: filtrada ? 'default' : (clickable ? 'pointer' : 'default'),
                        transition: 'transform 0.15s, opacity 0.2s, filter 0.2s',
                        '&:hover': (!filtrada && clickable) ? { transform: 'scale(1.1)', boxShadow: 2 } : {},
                      }}
                    >
                      {st.label}
                    </Box>
                  );
                  return celda.detalle ? (
                    <Tooltip key={d} title={`${celda.estado.toUpperCase()} · ${celda.detalle}`} arrow>
                      {cell}
                    </Tooltip>
                  ) : (
                    <Box key={d}>{cell}</Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
